import { useMemo, useRef, useState } from 'react';
import {
  MatterStatus,
  RetainerStatus,
  type RetainerFieldsFragment,
} from '~/gql/graphql';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import { matterHasActiveExpertParticipation } from '~/modules/matters/matterCardUtils';
import {
  expertHasActiveParticipationInMatterExperts,
  getHiredAndInvitedExpertIds,
} from '~/modules/matters/matterExpertUtils';
import { UserRole, type User } from '~/shared/types/connect';

export enum SortOption {
  UpdatedAtDesc = 'updatedAt-desc',
  NextDeadlineAsc = 'nextDeadline-asc',
  TitleAsc = 'title-asc',
}

export enum FilterByOption {
  All = 'all',
  PendingApproval = 'pendingApproval',
}

export const FILTER_BY_LABELS: Record<FilterByOption, string> = {
  [FilterByOption.All]: 'Show All',
  [FilterByOption.PendingApproval]: 'Pending approval',
};

export const SORT_OPTION_LABELS: Record<SortOption, string> = {
  [SortOption.UpdatedAtDesc]: 'Last updated',
  [SortOption.NextDeadlineAsc]: 'Next deadline',
  [SortOption.TitleAsc]: 'Title A–Z',
};

export function labelMapToOptions<T extends string>(
  labels: Record<T, string>,
): { value: T; label: string }[] {
  return (Object.entries(labels) as [T, string][]).map(([value, label]) => ({
    value,
    label,
  }));
}

export const SORT_OPTIONS: { value: SortOption; label: string }[] =
  labelMapToOptions(SORT_OPTION_LABELS);

const SORT_CONFIG: Record<
  SortOption,
  { field: string; order: 'asc' | 'desc' }
> = {
  [SortOption.UpdatedAtDesc]: { field: 'updatedAt', order: 'desc' },
  [SortOption.NextDeadlineAsc]: { field: 'nextDeadline', order: 'asc' },
  [SortOption.TitleAsc]: { field: 'title', order: 'asc' },
};

export const MATTERS_PAGE_SIZE = 5;

function toSafeTimestamp(value: number, order: 'asc' | 'desc'): number {
  if (!Number.isNaN(value)) {
    return value;
  }
  return order === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
}

function sortMatters(
  list: MatterViewModel[],
  sortBy: SortOption,
): MatterViewModel[] {
  const copy = [...list];
  const { field, order } = SORT_CONFIG[sortBy];
  copy.sort((matterA, matterB) => {
    if (field === 'nextDeadline') {
      const aVal = toSafeTimestamp(
        matterA.nextDeadline
          ? new Date(matterA.nextDeadline).getTime()
          : Number.MAX_SAFE_INTEGER,
        order,
      );
      const bVal = toSafeTimestamp(
        matterB.nextDeadline
          ? new Date(matterB.nextDeadline).getTime()
          : Number.MAX_SAFE_INTEGER,
        order,
      );
      const orderMultiplier = order === 'asc' ? 1 : -1;
      return (aVal - bVal) * orderMultiplier;
    }
    if (field === 'title') {
      const aVal = matterA.title;
      const bVal = matterB.title;
      if (order === 'asc') {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    }
    if (field === 'updatedAt') {
      const aVal = toSafeTimestamp(
        new Date(matterA.updatedAt).getTime(),
        order,
      );
      const bVal = toSafeTimestamp(
        new Date(matterB.updatedAt).getTime(),
        order,
      );
      const orderMultiplier = order === 'asc' ? 1 : -1;
      return (aVal - bVal) * orderMultiplier;
    }
    return 0;
  });
  return copy;
}

export interface UseMattersListParams {
  matters: MatterViewModel[];
  currentUser: User | null;
  mattersSubTab: 'active' | 'inactive' | 'closed';
  sortBy: SortOption;
  filterBy: FilterByOption;
  expertFilterId: string | null;
  /** When currentUser is Expert, filter matters by this attorney id. */
  attorneyFilterId?: string | null;
  getRetainersForMatter: (matterId: string) => RetainerFieldsFragment[];
  getExpertById: (id: string) => User | undefined;
  getAttorneyById?: (id: string) => User | undefined;
}

export interface UseMattersListResult {
  myMatters: MatterViewModel[];
  filteredMatters: MatterViewModel[];
  visibleMatters: MatterViewModel[];
  hasMoreMatters: boolean;
  loadMore: () => void;
  activeCount: number;
  inactiveCount: number;
  closedCount: number;
  expertOptions: { id: string; expert: User }[];
  attorneyOptions: { id: string; attorney: User }[];
}

export function useMattersList({
  matters,
  currentUser,
  mattersSubTab,
  sortBy,
  filterBy,
  expertFilterId,
  attorneyFilterId = null,
  getRetainersForMatter,
  getExpertById,
  getAttorneyById,
}: UseMattersListParams): UseMattersListResult {
  const [visibleCount, setVisibleCount] = useState(MATTERS_PAGE_SIZE);

  const filterKey = `${mattersSubTab}-${sortBy}-${filterBy}-${expertFilterId}-${
    attorneyFilterId ?? ''
  }`;
  const prevFilterKey = useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    setVisibleCount(MATTERS_PAGE_SIZE);
  }

  const myMatters = useMemo(
    () =>
      matters.filter(
        (matter) =>
          (currentUser?.role === UserRole.ATTORNEY &&
            matter.attorneyId === currentUser.id) ||
          (currentUser?.role === UserRole.EXPERT &&
            Boolean(
              matterHasActiveExpertParticipation(matter, currentUser.id),
            )),
      ),
    [matters, currentUser],
  );

  const mattersScopedByExpert = useMemo(() => {
    if (expertFilterId === null) {
      return myMatters;
    }
    return myMatters.filter((matter) =>
      expertHasActiveParticipationInMatterExperts(
        matter.matterExperts,
        expertFilterId,
      ),
    );
  }, [myMatters, expertFilterId]);

  const filteredByStatus = useMemo(
    () =>
      mattersScopedByExpert.filter((matter) => {
        if (mattersSubTab === 'active') {
          return matter.status === MatterStatus.Active;
        }
        if (mattersSubTab === 'inactive') {
          return matter.status === MatterStatus.Inactive;
        }
        return matter.status === MatterStatus.Closed;
      }),
    [mattersScopedByExpert, mattersSubTab],
  );

  const expertOptions = useMemo(() => {
    if (currentUser?.role !== 'Attorney') {
      return [];
    }
    const ids = [
      ...new Set(
        myMatters.flatMap((matter) =>
          getHiredAndInvitedExpertIds(matter.matterExperts),
        ),
      ),
    ];
    return ids
      .map((id) => ({ id, expert: getExpertById(id) }))
      .filter((option): option is { id: string; expert: User } =>
        Boolean(option.expert),
      )
      .sort((optionA, optionB) =>
        (optionA.expert.name ?? '').localeCompare(optionB.expert.name ?? ''),
      );
  }, [currentUser?.role, myMatters, getExpertById]);

  const filteredByAttorney = useMemo(() => {
    if (currentUser?.role !== 'Expert' || attorneyFilterId === null) {
      return filteredByStatus;
    }
    return filteredByStatus.filter(
      (matter) => matter.attorneyId === attorneyFilterId,
    );
  }, [filteredByStatus, currentUser?.role, attorneyFilterId]);

  const attorneyOptions = useMemo(() => {
    if (
      currentUser?.role !== 'Expert' ||
      getAttorneyById === null ||
      getAttorneyById === undefined
    ) {
      return [];
    }
    const ids = [
      ...new Set(
        myMatters
          .map((matter) => matter.attorneyId)
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    return ids
      .map((id) => ({ id, attorney: getAttorneyById(id) }))
      .filter((option): option is { id: string; attorney: User } =>
        Boolean(option.attorney),
      )
      .sort((optionA, optionB) =>
        (optionA.attorney.name ?? '').localeCompare(
          optionB.attorney.name ?? '',
        ),
      );
  }, [currentUser?.role, myMatters, getAttorneyById]);

  const filteredAfterPartyFilter =
    currentUser?.role === 'Expert' ? filteredByAttorney : filteredByStatus;

  const filteredByRetainer = useMemo(() => {
    if (filterBy === FilterByOption.All) {
      return filteredAfterPartyFilter;
    }
    return filteredAfterPartyFilter.filter((matter) =>
      getRetainersForMatter(matter.id).some(
        (retainer) => retainer.status === RetainerStatus.Pending,
      ),
    );
  }, [filteredAfterPartyFilter, filterBy, getRetainersForMatter]);

  const filteredMatters = useMemo(
    () => sortMatters(filteredByRetainer, sortBy),
    [filteredByRetainer, sortBy],
  );

  const visibleMatters = useMemo(
    () => filteredMatters.slice(0, visibleCount),
    [filteredMatters, visibleCount],
  );

  const hasMoreMatters = filteredMatters.length > visibleCount;

  const loadMore = () => {
    setVisibleCount((currentCount) => currentCount + MATTERS_PAGE_SIZE);
  };

  const { activeCount, inactiveCount, closedCount } = useMemo(
    () =>
      mattersScopedByExpert.reduce<{
        activeCount: number;
        inactiveCount: number;
        closedCount: number;
      }>(
        (counts, matter) => {
          if (matter.status === MatterStatus.Active) {
            counts.activeCount += 1;
          } else if (matter.status === MatterStatus.Inactive) {
            counts.inactiveCount += 1;
          } else if (matter.status === MatterStatus.Closed) {
            counts.closedCount += 1;
          }
          return counts;
        },
        { activeCount: 0, inactiveCount: 0, closedCount: 0 },
      ),
    [mattersScopedByExpert],
  );

  return {
    myMatters,
    filteredMatters,
    visibleMatters,
    hasMoreMatters,
    loadMore,
    activeCount,
    inactiveCount,
    closedCount,
    expertOptions,
    attorneyOptions,
  };
}
